import { useCallback, useState } from 'react';
import Container from '@material-ui/core/Container';
import Grid, { GridSize } from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

import { useFormikContext } from 'formik';
import { DateTime } from "luxon";

import { ValuesModel } from '..';
import useStepper, { TypePlaceData } from '../../../hooks/useStepper';
import makeTransport from '../../../utils/functions/mockTransport';
import { makeFloor } from '../../../utils/formatters';
import { PencilIcon } from '../../../assets/icons';
import { TicketBackground } from '../../../assets/images';

const makeSummary = (values: ValuesModel, place_data: TypePlaceData) => {
  const luxonDate = DateTime.fromISO(values.date.toString()).setLocale('pt-BR');
  return ([
    {
      title: "Data do Agendamento",
      fields: {
        'Dia da Semana': luxonDate.weekdayLong,
        'Data': luxonDate.toLocaleString(),
      }
    },
    {
      title: "Seu Ambiente",
      fields: {
        'Prédio': place_data.buildingName,
        'Piso': makeFloor(values.floor),
        'Setor': place_data.sectionName,
        'Mesa': values.desk,
      }
    },
    {
      title: "Meio de Transporte",
      fields: {
        'Transporte': makeTransport(values.transport),
        // 'Garagem': values.floor,
        // 'Vaga': values.section,
      },
      breakpoints: {
        sm: 4,
      } as { [key: string]: GridSize }
    },
  ])
}

const Summary = () => {
  const { values } = useFormikContext<ValuesModel>();
  const { setStep, setEditMode, placeData } = useStepper();
  const isMax760Width = useMediaQuery('(max-width:759px)');
  const classes = useStyles();

  const [summary] = useState(makeSummary(values, placeData));

  const handleEdit = useCallback((index: number) => {
    setStep(index);
    setEditMode(true);
  }, [setStep, setEditMode]);

  return (
    <Container className={classes.container}>
      <Typography variant="h3">Verifique as informações para concluir seu agendamento.</Typography>

      <Grid container item xs={12} spacing={3} component="section" className={classes.contentContainer}>
        {summary.map((item, index) => {
          const content = Object.entries(item.fields);
          return (
            <Grid container item xs={12} spacing={2}>
              <Grid container item xs={12} alignItems="center">
                <Grid item xs={11}>
                  <Typography variant="h4">{item.title}</Typography>
                </Grid>
                <Grid item xs={1}>
                  <Button onClick={() => handleEdit(index)}>
                    <PencilIcon color="primary" />
                  </Button>
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={2}>
                {content.map(([key, value]) => (
                  <Grid item xs sm={!!item.breakpoints ? item.breakpoints.sm : 3}>
                    <Typography variant="h5">{key}</Typography>
                    <Typography>{value}</Typography>
                  </Grid>
                ))}
              </Grid>

              {(index !== summary.length - 1 && isMax760Width) &&
                <Divider variant="fullWidth" component="div" style={{ width: '100%' }} />
              }
            </Grid>
          )
        }
        )}
      </Grid>
    </Container>
  );
}

export default Summary;

const useStyles = makeStyles<Theme>((theme) =>
  createStyles({
    container: {
      padding: 0,
      maxWidth: 700,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',

      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0, 3),
      },

      '& h3': {
        fontSize: theme.typography.pxToRem(16),
        textAlign: 'center',
      },
    },

    contentContainer: {
      marginTop: theme.spacing(4),

      [theme.breakpoints.up(760)]: {
        padding: theme.spacing(2),
        background: `center / contain no-repeat url(${TicketBackground})`,
      },

      '& h4': {
        fontSize: theme.typography.pxToRem(20),
        fontWeight: theme.typography.fontWeightBold,
      },
      '& h5': {
        color: theme.palette.text.secondary,
        fontSize: theme.typography.pxToRem(13),
        fontWeight: theme.typography.fontWeightBold,
      },
      '& p': {
        textTransform: 'capitalize',
      }
    }
  })
);